import {
	computeNetBalanceForUser,
	formatBalanceBannerCopy,
	groupBalancesByMember,
	summarizeBalanceForBanner,
} from '@/utils/balanceCalculations'
import ConfirmationModal from '@components/ConfirmationModal'
import { PageHeader } from '@components/Page'
import PageLoader from '@components/PageLoader'
import {
	useFiles,
	useMe,
	usePaymentsOwed,
	usePayUps,
	useTrip,
	useTripMembers,
} from '@hooks'
import { useExpenses } from '@hooks/useExpenses'
import { downloadTextFile } from '@lib/download'
import { GetExpenseResponse, GetPayUpType } from '@orbit/api-types'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import ActivityFilterModal, {
	ActivityFilter,
	countFilterCategories,
	EMPTY_ACTIVITY_FILTER,
} from './ActivityFilterModal'
import ActivitySection from './ActivitySection/ActivitySection'
import AddExpenseModal from './AddExpenseModal'
import AddPayUpModal from './AddPayUpModal'
import { PayUpPrefill } from './payupForm'
import { buildPrefillFromBreakdown } from './balancesFormat'
import { buildLedgerCsv, buildLedgerFilename } from './ledgerExport'
import {
	ActivityColumn,
	BalancesPageContainer,
	ContentContainer,
	LoadingContainer,
	MembersColumn,
	MobileLayout,
	MobilePanel,
	SplitLayout,
} from './BalancesPage.styles'
import DeletePayUpConfirmModal from './DeletePayUpConfirmModal'
import EditExpenseModal from './EditExpenseModal'
import ExpenseDetailsModal from './ExpenseDetailsModal'
import MembersSection from './MembersSection/MembersSection'
import MobileTabs, { BalancesTab } from './MobileTabs'
import PayUpDetailsModal from './PayUpDetailsModal'
import { useBalancesMutations } from './useBalancesMutations'
import { useGroupedActivity } from './useGroupedActivity'
import { useSortedMembers } from './useSortedMembers'
import { ErrorNote } from '@components/Notice'

const BalancesPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const { data: trip, isLoading: tripLoading, error: tripError } = useTrip(tripId)
	const {
		data: paymentsOwedData,
		isLoading: paymentsLoading,
		error: paymentsError,
	} = usePaymentsOwed(tripId)
	const { data: membersData, isLoading: membersLoading, error: membersError } = useTripMembers(tripId)
	const { data: me } = useMe()
	const { data: expensesData, isLoading: expensesLoading, error: expensesError } = useExpenses(tripId)
	const { data: payUpsData, isLoading: payUpsLoading, error: payUpsError } = usePayUps(tripId)
	const { data: filesData } = useFiles(tripId)

	const members = useMemo(() => membersData?.members ?? [], [membersData])
	const paymentsOwed = useMemo(() => paymentsOwedData?.paymentsOwed ?? [], [paymentsOwedData])
	const expenses = useMemo(() => expensesData?.expenses ?? [], [expensesData])
	const payUps = useMemo(() => payUpsData?.payUps ?? [], [payUpsData])
	const defaultCurrencyCode = trip?.defaultCurrencyCode ?? 'usd'

	const fileUrlById = useMemo(() => {
		const map = new Map<string, string>()
		filesData?.files?.forEach(f => map.set(f.id, f.url))
		return map
	}, [filesData])

	const showCurrencyCode = useMemo(() => {
		const codes = new Set<string>()
		paymentsOwed.forEach(p => codes.add(p.currencyCode))
		payUps.forEach(p => codes.add(p.currencyCode))
		expenses.forEach(e => codes.add(e.currencyCode))
		return codes.size >= 2
	}, [paymentsOwed, payUps, expenses])

	const memberIds = useMemo(() => members.map(m => m.id), [members])
	const groups = useMemo(() => groupBalancesByMember(paymentsOwed, memberIds), [paymentsOwed, memberIds])

	const settledById = useMemo(() => {
		const map = new Map<number, boolean>()
		for (const g of groups) {
			map.set(g.userId, g.balancesPerCurrency.length === 0)
		}
		return map
	}, [groups])

	const sortedMembers = useSortedMembers(members, me, settledById)

	const [activityFilter, setActivityFilter] = useState<ActivityFilter>(EMPTY_ACTIVITY_FILTER)
	const [isActivityFilterModalOpen, setIsActivityFilterModalOpen] = useState(false)
	const activityFilterCount = countFilterCategories(activityFilter)

	const groupedActivity = useGroupedActivity(expenses, payUps, activityFilter)

	const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set())
	const [didInitExpansion, setDidInitExpansion] = useState(false)

	const [isAddExpenseModalOpen, setIsAddExpenseModalOpen] = useState(false)
	const [viewingExpense, setViewingExpense] = useState<GetExpenseResponse | null>(null)
	const [editingExpense, setEditingExpense] = useState<GetExpenseResponse | null>(null)
	const [deletingExpense, setDeletingExpense] = useState<GetExpenseResponse | null>(null)

	const [isAddPayUpModalOpen, setIsAddPayUpModalOpen] = useState(false)
	const [addPayUpPrefill, setAddPayUpPrefill] = useState<PayUpPrefill | undefined>(undefined)
	const [viewingPayUp, setViewingPayUp] = useState<GetPayUpType | null>(null)
	const [deletingPayUp, setDeletingPayUp] = useState<GetPayUpType | null>(null)

	const [mobileTab, setMobileTab] = useState<BalancesTab>('members')

	React.useEffect(() => {
		if (didInitExpansion || !me) return
		if (memberIds.length === 0) return
		setExpandedIds(new Set([me.id]))
		setDidInitExpansion(true)
	}, [didInitExpansion, me, memberIds])

	const toggleMemberExpansion = (memberId: number) => {
		setExpandedIds(prev => {
			const next = new Set(prev)
			if (next.has(memberId)) {
				next.delete(memberId)
			} else {
				next.add(memberId)
			}
			return next
		})
	}

	const closeAddPayUpModal = () => {
		setIsAddPayUpModalOpen(false)
		setAddPayUpPrefill(undefined)
	}

	const openAddPayUpModalWithPrefill = (prefill: PayUpPrefill) => {
		setAddPayUpPrefill(prefill)
		setIsAddPayUpModalOpen(true)
	}

	const openAddPayUpModal = () => {
		setAddPayUpPrefill(undefined)
		setIsAddPayUpModalOpen(true)
	}

	const mutations = useBalancesMutations({
		tripId,
		me,
		editingExpense,
		deletingExpense,
		deletingPayUp,
		onCloseAddExpense: () => setIsAddExpenseModalOpen(false),
		onCloseEditExpense: () => setEditingExpense(null),
		onCloseDeleteExpense: () => setDeletingExpense(null),
		onCloseAddPayUp: closeAddPayUpModal,
		onCloseDeletePayUp: () => setDeletingPayUp(null),
		onCloseViewingPayUp: () => setViewingPayUp(null),
	})

	if (tripLoading || paymentsLoading || membersLoading || expensesLoading || payUpsLoading) {
		return (
			<PageLoader label="Loading balances..." />
		)
	}

	if (tripError || paymentsError || membersError || expensesError || payUpsError) {
		return (
			<ErrorNote>
				Error loading balances:{' '}
				{tripError?.message ||
					paymentsError?.message ||
					membersError?.message ||
					expensesError?.message ||
					payUpsError?.message}
			</ErrorNote>
		)
	}

	if (!trip) {
		return <LoadingContainer>Trip not found</LoadingContainer>
	}

	const allSettled = groups.every(g => g.balancesPerCurrency.length === 0)

	const myBalanceSummary = me
		? summarizeBalanceForBanner(computeNetBalanceForUser(paymentsOwed, me.id))
		: { kind: 'settled' as const }
	const myBalanceCopy = formatBalanceBannerCopy(myBalanceSummary)
	const myTone: 'settled' | 'owe' | 'owed' | 'mixed' =
		myBalanceSummary.kind === 'settled'
			? 'settled'
			: myBalanceSummary.kind === 'singleDirection'
				? myBalanceSummary.direction
				: 'mixed'

	const canRecordPayup = members.length >= 2 && !!me

	const membersBlock = (
		<MembersSection
			sortedMembers={sortedMembers}
			groups={groups}
			expandedIds={expandedIds}
			allMembers={members}
			showCurrencyCode={showCurrencyCode}
			allSettled={allSettled}
			summaryLabel="Your total"
			summaryValue={myBalanceCopy}
			summaryTone={myTone}
			canRecordPayup={canRecordPayup}
			onToggleMember={toggleMemberExpansion}
			onRecordPayup={openAddPayUpModal}
			onRecordPayupForBreakdown={(memberId, item) =>
				openAddPayUpModalWithPrefill(buildPrefillFromBreakdown(memberId, item))
			}
		/>
	)

	// Ledger export: always the full trip, never the filtered view — a
	// silently-filtered snapshot reads as "the trip's money story" but isn't.
	const handleExportCsv = () => {
		downloadTextFile(
			buildLedgerFilename(trip?.name ?? '', new Date()),
			buildLedgerCsv({ expenses, payUps, members }),
		)
	}

	const activityBlock = (
		<ActivitySection
			groupedActivity={groupedActivity}
			activityFilter={activityFilter}
			activityFilterCount={activityFilterCount}
			members={members}
			me={me}
			fileUrlById={fileUrlById}
			showCurrencyCode={showCurrencyCode}
			onOpenFilter={() => setIsActivityFilterModalOpen(true)}
			onExportCsv={handleExportCsv}
			canExportCsv={membersData !== undefined && (expenses.length > 0 || payUps.length > 0)}
			onAddExpense={() => setIsAddExpenseModalOpen(true)}
			onClickExpense={setViewingExpense}
			onClickPayUp={setViewingPayUp}
		/>
	)

	return (
		<BalancesPageContainer>
			<ContentContainer>
				<PageHeader title="Expenses & pay ups" backTo={{ path: `/trips/${tripId}`, label: trip.name }} />
				<SplitLayout>
					<MembersColumn>{membersBlock}</MembersColumn>
					<ActivityColumn>{activityBlock}</ActivityColumn>
				</SplitLayout>

				<MobileLayout>
					<MobileTabs value={mobileTab} onChange={setMobileTab} />
					<MobilePanel $active={mobileTab === 'members'}>{membersBlock}</MobilePanel>
					<MobilePanel $active={mobileTab === 'activity'}>{activityBlock}</MobilePanel>
				</MobileLayout>
			</ContentContainer>

			<ActivityFilterModal
				isOpen={isActivityFilterModalOpen}
				onClose={() => setIsActivityFilterModalOpen(false)}
				onApply={filter => {
					setActivityFilter(filter)
					setIsActivityFilterModalOpen(false)
				}}
				members={members}
				currentUserId={me?.id}
				currentFilter={activityFilter}
			/>

<AddExpenseModal
				isOpen={isAddExpenseModalOpen}
				onClose={() => setIsAddExpenseModalOpen(false)}
				onSubmit={mutations.handleAddExpense}
				members={members}
				defaultCurrencyCode={defaultCurrencyCode}
				isSubmitting={mutations.isExpenseSubmitting}
			/>

			<ExpenseDetailsModal
				isOpen={viewingExpense !== null}
				onClose={() => setViewingExpense(null)}
				onEdit={() => {
					if (!viewingExpense) return
					setEditingExpense(viewingExpense)
					setViewingExpense(null)
				}}
				onDelete={() => {
					if (!viewingExpense) return
					setDeletingExpense(viewingExpense)
					setViewingExpense(null)
				}}
				expense={viewingExpense}
				members={members}
				currentUserId={me?.id}
				fileUrlById={fileUrlById}
				showCurrencyCode={showCurrencyCode}
			/>

			{editingExpense && tripId && (
				<EditExpenseModal
					isOpen={!!editingExpense}
					onClose={() => setEditingExpense(null)}
					onSubmit={mutations.handleEditExpense}
					onDelete={() => setDeletingExpense(editingExpense)}
					expense={editingExpense}
					tripId={tripId}
					members={members}
					isSubmitting={mutations.isEditExpenseSubmitting}
				/>
			)}

			<ConfirmationModal
				isOpen={!!deletingExpense}
				onClose={() => setDeletingExpense(null)}
				onConfirm={mutations.handleDeleteExpense}
				title="Delete Expense"
				message={`Are you sure you want to delete "${deletingExpense?.name}"? This action cannot be undone.`}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
			/>

			{me && (
				<AddPayUpModal
					isOpen={isAddPayUpModalOpen}
					onClose={closeAddPayUpModal}
					onSubmit={mutations.handleAddPayUp}
					members={members}
					currentUserId={me.id}
					defaultCurrencyCode={defaultCurrencyCode}
					prefill={addPayUpPrefill}
					isSubmitting={mutations.isPayUpSubmitting}
				/>
			)}

			<PayUpDetailsModal
				isOpen={viewingPayUp !== null}
				onClose={() => setViewingPayUp(null)}
				onDelete={() => {
					if (!viewingPayUp) return
					setDeletingPayUp(viewingPayUp)
					setViewingPayUp(null)
				}}
				payUp={viewingPayUp}
				members={members}
				currentUserId={me?.id}
				showCurrencyCode={showCurrencyCode}
			/>

			{me && (
				<DeletePayUpConfirmModal
					isOpen={deletingPayUp !== null}
					onClose={() => setDeletingPayUp(null)}
					onConfirm={mutations.handleDeletePayUp}
					payUp={deletingPayUp}
					members={members}
					currentUserId={me.id}
					showCurrencyCode={showCurrencyCode}
					isSubmitting={mutations.isDeletingPayUpPending}
				/>
			)}
		</BalancesPageContainer>
	)
}

export default BalancesPage
